import { useKestrel } from 'features/kestrelViewer'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { Vector2 } from 'three'
import { KVCameraView } from 'types'

import { DeleteCameraViewButton } from './DeleteCameraViewButton'
import { EditCameraViewButton } from './EditCameraViewButton'

const StyledCameraViewEntry = styled.div<{
  $selected: boolean
}>`
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 0.25rem;
  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const PrimaryLineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: center;
  overflow: hidden;
`

const LeftSideInfo = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  max-width: calc(100% - 80px);
  overflow: hidden;
`

const CameraViewLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.forecast.text1};
`

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  position: absolute;
  right: 0;
`

export const CameraViewEntry: FC<{
  cameraView: KVCameraView
  selectedMarkupId: string | undefined
}> = ({ cameraView, selectedMarkupId }) => {
  const isSelected = cameraView.id === selectedMarkupId
  const [isHovered, setIsHovered] = useState(false)
  const { setPopupMenuStartPosition, setSelectedMarkup, sendKVCommand } =
    useKestrel()

  const handleEntryClick = (e: React.MouseEvent) => {
    // Check if the click was on an action button (edit or delete)
    const target = e.target as HTMLElement
    const isActionButton = target.closest('[data-action-button]')

    if (isActionButton) {
      // If clicking on an action button, don't zoom
      return
    }

    // Zoom to camera view when clicking anywhere else on the entry
    setSelectedMarkup(undefined)
    if (sendKVCommand) {
      sendKVCommand({
        command: 'zoomToCameraView',
        cameraViewId: cameraView.id,
        cameraViewPosition: cameraView.cameraPosition,
        cameraViewRotation: cameraView.cameraRotation,
      })
    }
  }

  const handleEntryRightClick = (e: React.MouseEvent) => {
    // Right click still opens the edit menu
    if (setPopupMenuStartPosition) {
      setPopupMenuStartPosition(new Vector2(10, e.clientY - 40))
    }
    const newSelectedMarkup =
      selectedMarkupId === cameraView.id ? undefined : cameraView
    setSelectedMarkup(newSelectedMarkup)
  }

  return (
    <StyledCameraViewEntry
      $selected={isSelected}
      onClick={handleEntryClick}
      onContextMenu={handleEntryRightClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <PrimaryLineContainer>
        <LeftSideInfo>
          <CameraViewLabel>
            {cameraView.name || 'New Camera View'}
          </CameraViewLabel>
        </LeftSideInfo>
        {isHovered && (
          <ActionsContainer>
            <EditCameraViewButton cameraView={cameraView} />
            <DeleteCameraViewButton cameraViewId={cameraView.id} />
          </ActionsContainer>
        )}
      </PrimaryLineContainer>
    </StyledCameraViewEntry>
  )
}
